import { lazy, Suspense, type ReactElement } from "react";
import { createBrowserRouter } from "react-router-dom";
import { AppShell } from "@/app/layout/AppShell";
import { BareLayout } from "@/app/layout/BareLayout";
import { AuthLayout } from "@/app/layout/AuthLayout";
import { ProtectedRoute } from "@/app/routes/ProtectedRoute";
import { RootLayout } from "@/app/routes/RootLayout";
import { LoadingScreen } from "@/components/LoadingScreen";
import { HomePage } from "@/pages/HomePage";
import { LoginPage } from "@/pages/LoginPage";
import { DashboardPage } from "@/pages/DashboardPage";
import { N2Page } from "@/pages/N2Page";
import { N3Page } from "@/pages/N3Page";
import { IndicatorsPage } from "@/pages/IndicatorsPage";
import { MeasuresPage } from "@/pages/MeasuresPage";
import { SettingsPage } from "@/pages/SettingsPage";
import { SourceReportPage } from "@/pages/SourceReportPage";
import { NotFoundPage } from "@/pages/NotFoundPage";
import { UnauthorizedPage } from "@/pages/UnauthorizedPage";
import { ForbiddenPage } from "@/pages/ForbiddenPage";
import { LegalPage } from "@/pages/LegalPage";
import { HubShell } from "@/features/hub/components/HubShell";
import { HubOverviewPage } from "@/features/hub/pages/HubOverviewPage";
import { HubServicesPage } from "@/features/hub/pages/HubServicesPage";
import { HubIndicatorsPage } from "@/features/hub/pages/HubIndicatorsPage";
import { HubCollectPage } from "@/features/hub/pages/HubCollectPage";
import { HubReportPage } from "@/features/hub/pages/HubReportPage";
import type { Capability } from "@/services/auth/permissions";

// Code-split the review feature: it is the first sizeable business vertical
// and has no reason to inflate the initial bundle for every other route.
const ReviewListPage = lazy(() =>
  import("@/features/review/pages/ReviewListPage").then((m) => ({ default: m.ReviewListPage })),
);
const ReviewDetailPage = lazy(() =>
  import("@/features/review/pages/ReviewDetailPage").then((m) => ({ default: m.ReviewDetailPage })),
);

// Phase 9C.3B.7 — same rationale as the review feature above, applied to
// the N1 Cockpit (7 widgets + mappers + i18n strings): it was still a
// static import (`PHASE_9C3B0_TECHNICAL_AUDIT.md` §0.5,
// `N1_PERFORMANCE_PLAN.md` § "Bundle impact mesuré", which explicitly
// deferred this decision to this phase). No behavior change for the user
// beyond the existing `LoadingScreen` fallback already used by every other
// lazy route (same `Suspense` wrapper via `lazyRoute()` below) while the
// chunk downloads — same as `/review` today.
const N1CockpitPage = lazy(() =>
  import("@/features/n1/pages/N1CockpitPage").then((m) => ({ default: m.N1CockpitPage })),
);

function protect(
  element: ReactElement,
  requireCapability?: Capability,
  options?: { requireAuth?: boolean },
) {
  return (
    <ProtectedRoute
      {...(requireCapability != null ? { requireCapability } : {})}
      requireAuth={options?.requireAuth ?? true}
    >
      {element}
    </ProtectedRoute>
  );
}

/** Public consultation — no session required; capability still enforced (PUBLIC caps). */
function publicPage(element: ReactElement, requireCapability?: Capability) {
  return protect(element, requireCapability, { requireAuth: false });
}

function lazyRoute(element: ReactElement) {
  return <Suspense fallback={<LoadingScreen />}>{element}</Suspense>;
}

export const router = createBrowserRouter([
  {
    element: <RootLayout />,
    children: [
      {
        path: "/",
        element: <AppShell />,
        children: [
          { index: true, element: publicPage(<HomePage />, "nav.home") },
          {
            path: "politique-de-confidentialite",
            element: publicPage(<LegalPage />, "nav.home"),
          },
          { path: "dashboard", element: publicPage(<DashboardPage />, "nav.dashboard") },
          { path: "indicators", element: publicPage(<IndicatorsPage />, "nav.indicators") },
          { path: "measures", element: publicPage(<MeasuresPage />, "nav.measures") },
          { path: "review", element: protect(lazyRoute(<ReviewListPage />), "nav.review") },
          {
            path: "review/:proposalId",
            element: protect(lazyRoute(<ReviewDetailPage />), "nav.review"),
          },
          { path: "n1", element: publicPage(lazyRoute(<N1CockpitPage />), "nav.n1") },
          { path: "n2", element: publicPage(<N2Page />, "nav.indicators") },
          { path: "n3", element: publicPage(<N3Page />, "nav.measures") },
          {
            path: "source-report",
            element: protect(<SourceReportPage />, "ui.adminExtras"),
          },
          { path: "settings", element: protect(<SettingsPage />, "nav.settings") },
        ],
      },
      {
        // Hub prototype — own chrome (design mock), parallel to existing AppShell views.
        path: "hub",
        element: publicPage(<HubShell />, "nav.hub"),
        children: [
          { index: true, element: <HubOverviewPage /> },
          { path: "services", element: <HubServicesPage /> },
          { path: "indicateurs", element: <HubIndicatorsPage /> },
          { path: "collecte", element: <HubCollectPage /> },
          { path: "rapport", element: <HubReportPage /> },
        ],
      },
      {
        // Authentication — chromeless, nature watermark background.
        element: <AuthLayout />,
        children: [{ path: "login", element: <LoginPage /> }],
      },
      {
        // Chromeless states — no header/sidebar/breadcrumb/footer, centered.
        element: <BareLayout />,
        children: [
          { path: "unauthorized", element: <UnauthorizedPage /> },
          { path: "forbidden", element: <ForbiddenPage /> },
          { path: "*", element: <NotFoundPage /> },
        ],
      },
    ],
  },
]);
